import React from 'react';
import { useSet } from 'react-use';
import { Box } from '../../../../../../../../../CommonComponents/OPL/Box/Box';
import { HtmlTable } from '../../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Icon } from '../../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Text } from '../../../../../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { formatCurrency } from '../../../../../../../../../CommonUtils/formatNumber';
import { RealizedGainLossDTO } from '../../../../../../../../api/useRealizedGainLossQuery';
import { PerformanceTableRow } from '../../../../../../../../components/PerformanceDetails/PerformanceDetails.styles';

interface Props {
  investments: RealizedGainLossDTO[];
}
export const MobileInvestmentTable = ({ investments }: Props) => {
  const [, { has: isRowExpanded, toggle: toggleRow }] = useSet<string>();

  return (
    <HtmlTable columnSpacing="small" variant="bordered">
      <table css="table-layout: fixed">
        <thead>
          <tr>
            <th>
              <Text variant="tableHeader" textAlign="left">
                Name
              </Text>
            </th>
            <th>
              <Text variant="tableHeader" textAlign="right">
                Total G/L
              </Text>
            </th>
          </tr>
        </thead>

        <tbody>
          {investments.map(investment => {
            const isExpanded = isRowExpanded(investment.name);

            return (
              <React.Fragment key={investment.name}>
                <PerformanceTableRow
                  isExpanded={isExpanded}
                  variant="clickable"
                  onClick={() => {
                    toggleRow(investment.name);
                  }}
                >
                  <td>
                    <Box display="flex" alignItems="center">
                      <Icon
                        className={isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
                        color="neutral.400"
                        width="22px"
                      />

                      <Box display="flex" flexDirection="column">
                        <TruncatedText
                          fontWeight={500}
                          color="neutral.700"
                          textAlign="left"
                          width="100%"
                        >
                          {investment.ticker}
                        </TruncatedText>

                        <TruncatedText
                          fontWeight={400}
                          color="neutral.500"
                          textAlign="left"
                          width="100%"
                          fontSize="small"
                        >
                          {investment.name}
                        </TruncatedText>
                      </Box>
                    </Box>
                  </td>

                  <td>
                    <Text
                      textAlign="right"
                      variant="tabular"
                      fontWeight={600}
                      color={investment.total < 0 ? 'error.500' : 'neutral.700'}
                    >
                      {formatCurrency(investment.total, { decimalPlaces: 2 })}
                    </Text>
                  </td>
                </PerformanceTableRow>

                {isExpanded && (
                  <>
                    <tr>
                      <td>
                        <Text
                          fontWeight={500}
                          color="neutral.500"
                          textAlign="left"
                          width="100%"
                          ml="24"
                        >
                          Proceedes
                        </Text>
                      </td>

                      <td>
                        <Text
                          textAlign="right"
                          variant="tabular"
                          color={investment.proceeds < 0 ? 'error.600' : 'neutral.500'}
                        >
                          {formatCurrency(investment.proceeds, { decimalPlaces: 2 })}
                        </Text>
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <Text
                          fontWeight={500}
                          color="neutral.500"
                          textAlign="left"
                          width="100%"
                          ml="24"
                        >
                          Cost Basis
                        </Text>
                      </td>

                      <td>
                        <Text
                          textAlign="right"
                          variant="tabular"
                          color={investment.costBasis < 0 ? 'error.600' : 'neutral.500'}
                        >
                          {formatCurrency(investment.costBasis, { decimalPlaces: 2 })}
                        </Text>
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <Text
                          fontWeight={500}
                          color="neutral.500"
                          textAlign="left"
                          width="100%"
                          ml="24"
                        >
                          Short Term G/L
                        </Text>
                      </td>

                      <td>
                        <Text
                          textAlign="right"
                          variant="tabular"
                          color={investment.shortTerm < 0 ? 'error.600' : 'neutral.500'}
                        >
                          {formatCurrency(investment.shortTerm, { decimalPlaces: 2 })}
                        </Text>
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <Text
                          fontWeight={500}
                          color="neutral.500"
                          textAlign="left"
                          width="100%"
                          ml="24"
                        >
                          Long Term G/L
                        </Text>
                      </td>

                      <td>
                        <Text
                          textAlign="right"
                          variant="tabular"
                          color={investment.longTerm < 0 ? 'error.600' : 'neutral.500'}
                        >
                          {formatCurrency(investment.longTerm, { decimalPlaces: 2 })}
                        </Text>
                      </td>
                    </tr>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </HtmlTable>
  );
};
